import { useCallback, useEffect } from "react";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { useKeyPress } from "@/core/keyPressManager";
import { PlayIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import { Layer } from "@/domains/createCharacter/constants/layer";
import {
  isReadyToCreateCharacterSelector,
  nextStep,
  personalDataErrorsSelector,
  setCorrectStep,
  stepSelector,
  stepValuesSelector,
} from "@/domains/createCharacter/slice";
import { currLayerSelector } from "@/domains/layer/slice";
import toast from "@/domains/toasts/helpers/toast";
import useCreateCharacter from "@/domains/createCharacter/hooks/useCreateCharacter";
import KeyCode from "@/assets/constants/key-code";

export default function CreateCharacterStartGameContainer() {
  const dispatch = useAppDispatch();
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();
  const { createCharacter, isPending } = useCreateCharacter();
  const personalDataErrors = useAppSelector(personalDataErrorsSelector);
  const isReadyToCreateCharacter = useAppSelector(isReadyToCreateCharacterSelector);
  const currLayer = useAppSelector(currLayerSelector);
  const step = useAppSelector(stepSelector);
  const stepValues = useAppSelector(stepValuesSelector);

  const startGameHandler = useCallback(async () => {
    if (!isReadyToCreateCharacter) {
      toast.game.warning("Надо пройти все шаги создания персонажа");
      return;
    }
    if (personalDataErrors) {
      toast.game.warning(
        `Некорректный ${personalDataErrors[0].path} (${personalDataErrors[0].message})`,
      );
      return;
    }
    await createCharacter();
  }, [personalDataErrors, isReadyToCreateCharacter, createCharacter]);

  const keyboardHandler = useCallback(
    async (keyCode: KeyCode) => {
      if (currLayer !== Layer) return;
      switch (keyCode) {
        case KeyCode.SPACE: {
          if (step === 0 && !personalDataErrors) {
            dispatch(
              setCorrectStep({
                step: 0,
                isCorrect: true,
              }),
            );
            dispatch(nextStep());
          }

          if (step > 0 && step < stepValues.length - 1) {
            dispatch(
              setCorrectStep({
                step,
                isCorrect: true,
              }),
            );
            dispatch(nextStep());
          }

          if (step === stepValues.length - 1) {
            await startGameHandler();
          }
          break;
        }
        default:
          break;
      }
    },
    [currLayer, step, personalDataErrors, stepValues, dispatch, startGameHandler],
  );

  useEffect(() => {
    const key = "space-click-handler";
    subscribeKeyDown(key, keyboardHandler);

    return () => {
      unsubscribeKeyDown(key);
    };
  }, [subscribeKeyDown, keyboardHandler, unsubscribeKeyDown]);

  return (
    <div className={clsx("flex flex-col gap-1 card rounded-[0.75rem] p-2 w-[21rem]")}>
      <Button
        variant={isReadyToCreateCharacter ? "solid" : "soft"}
        onClick={startGameHandler}
        loading={isPending}
        disabled={isPending}
      >
        <PlayIcon size={20} className="mr-2.5" />
        Начать игру
      </Button>
      <p className="text-center text-15 text-white/50 font-medium py-1">
        Создайте персонажа для начала игры
      </p>
    </div>
  );
}
